// src/utils/agendaUtils.js

export function parseHora(str) {
  if (!str || typeof str !== 'string') return 0;
  const parts = str.split(/[:\s-]/);
  const timePart = parts[0] + (parts[1] ? ':' + parts[1] : '');
  const [h, m] = timePart.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/**
 * Nueva firma: en lugar de 'isToday' y 'minNotice', recibimos 'dayDate' y 'minBookingDate'.
 * dayDate: Objeto Date del día que estamos generando (a las 00:00).
 * minBookingDate: Objeto Date con la fecha/hora límite (Ahora + Antelación).
 */
export function generarBloquesLibres(from, to, serviceDuration, baseStep, reservasDelDia = {}, dayDate = null, strategy = 'flexible', minBookingDate = null) {
  
  const bloques = [];
  if (!from || !to || !serviceDuration || !baseStep) return bloques;

  const duration = Number(serviceDuration);
  const step = Number(baseStep);
  if (isNaN(duration) || isNaN(step) || duration <= 0 || step <= 0) return bloques;

  const startOfDayMinutes = parseHora(from);
  const endOfDayMinutes = parseHora(to);

  // Preparamos la fecha base para comparaciones (si se provee)
  // Clonamos dayDate para no mutarlo
  const baseDate = dayDate ? new Date(dayDate) : null; 

  const bookedIntervals = Object.keys(reservasDelDia).map(rangeLabel => {
      const [r1, r2] = rangeLabel.split(' - ').map(parseHora);
      return { start: r1, end: r2 };
  }).sort((a, b) => a.start - b.start);

  // --- LÓGICA HÍBRIDA ---
  if (strategy === 'hybrid') {
    const allIntervals = [
        { start: -Infinity, end: startOfDayMinutes },
        ...bookedIntervals,
        { start: endOfDayMinutes, end: Infinity }
    ];

    for (let i = 0; i < allIntervals.length - 1; i++) {
        const windowStart = allIntervals[i].end;
        const windowEnd = allIntervals[i + 1].start;
        const windowDuration = windowEnd - windowStart;

        if (windowDuration < duration) continue;

        const isSmallWindow = windowDuration < duration * 3;
        let t = windowStart;
        while (t + duration <= windowEnd) {
            // VERIFICACIÓN DE ANTELACIÓN (Cross-day compatible)
            if (baseDate && minBookingDate) {
                const slotTime = new Date(baseDate);
                slotTime.setHours(0, t, 0, 0); // Sumamos los minutos del bloque al inicio del día
                if (slotTime < minBookingDate) {
                    t += step;
                    continue; // Saltamos este bloque porque es demasiado pronto
                }
            }

            const slotStart = t;
            const slotEnd = slotStart + duration;
            const hh1 = String(Math.floor(slotStart / 60)).padStart(2, '0');
            const mm1 = String(slotStart % 60).padStart(2, '0');
            const hh2 = String(Math.floor(slotEnd / 60)).padStart(2, '0');
            const mm2 = String(slotEnd % 60).padStart(2, '0');
            
            bloques.push({
                label: `${hh1}:${mm1}`,
                range: `${hh1}:${mm1} - ${hh2}:${mm2}`
            });
            
            if (isSmallWindow) break;
            t += step;
        }
    }
  } 
  // --- LÓGICA FLEXIBLE ---
  else {
    let t = startOfDayMinutes;
    while (t + duration <= endOfDayMinutes) {
      // VERIFICACIÓN DE ANTELACIÓN
      if (baseDate && minBookingDate) {
          const slotTime = new Date(baseDate);
          slotTime.setHours(0, t, 0, 0);
          if (slotTime < minBookingDate) {
              t += step;
              continue;
          }
      }

      const slotStart = t;
      const slotEnd = t + duration;

      const isOccupied = bookedIntervals.some(booked => 
          (slotStart < booked.end && slotEnd > booked.start)
      );
      
      if (!isOccupied) {
          const hh1 = String(Math.floor(slotStart / 60)).padStart(2, '0');
          const mm1 = String(slotStart % 60).padStart(2, '0');
          const hh2 = String(Math.floor(slotEnd / 60)).padStart(2, '0');
          const mm2 = String(slotEnd % 60).padStart(2, '0');
          bloques.push({
              label: `${hh1}:${mm1}`,
              range: `${hh1}:${mm1} - ${hh2}:${mm2}`
          });
      }
      t += step;
    }
  }
  
  return Array.from(new Map(bloques.map(b => [b.label, b])).values())
              .sort((a, b) => parseHora(a.label) - parseHora(b.label));
}

export function generarTodosLosBloquesPosibles(from, to, dur) {
  const bloques = [];
  if (!from || !to || !dur) return bloques;
  const duration = Number(dur) || 30;
  let t = parseHora(from);
  const end = parseHora(to);
  while (t + duration <= end) {
    const t2 = t + duration;
    const hh1 = String(Math.floor(t / 60)).padStart(2, '0');
    const mm1 = String(t % 60).padStart(2, '0');
    const hh2 = String(Math.floor(t2 / 60)).padStart(2, '0');
    const mm2 = String(t2 % 60).padStart(2, '0');
    bloques.push(`${hh1}:${mm1} - ${hh2}:${mm2}`);
    t += duration;
  }
  return bloques;
}